import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/home';
import { categoriesData, type CategoryData } from '@/mocks/categories';

const statusLabels: Record<string, { text: string; className: string }> = {
  live: { text: '直播中', className: 'bg-red-50 text-red-600 border-red-200' },
  upcoming: { text: '即将开始', className: 'bg-accent-50 text-accent-700 border-accent-200' },
  finished: { text: '已结束', className: 'bg-background-200 text-foreground-500 border-background-300' },
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'football');
  const [currentCategory, setCurrentCategory] = useState<CategoryData | null>(null);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [activeSlug]);

  useEffect(() => {
    const found = categoriesData.find((c) => c.slug === activeSlug);
    setCurrentCategory(found || categoriesData[0]);
  }, [activeSlug]);

  return (
    <>
      <Navbar />
      <main className="pt-20 pb-14 min-h-screen bg-background-50">
        <div className="w-full px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '赛事分类' },
                { label: currentCategory?.name || '' },
              ]}
            />
          </div>

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
              赛事分类
            </h1>
            <p className="text-sm text-foreground-500">选择赛事类型，开启精彩竞猜之旅</p>
          </div>

          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => setActiveSlug(cat.slug)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/60'
                }`}
              >
                <i className={`${cat.icon} text-base`}></i>
                {cat.name}
                {activeSlug === cat.slug && (
                  <span className="text-xs opacity-80">{cat.matchCount}+</span>
                )}
              </Link>
            ))}
          </div>

          {currentCategory && (
            <>
              <div className="bg-background-100 border border-background-200/60 rounded-lg p-5 mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                    <i className={`${currentCategory.icon} text-xl`}></i>
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-foreground-800">{currentCategory.name}赛事</h2>
                    <p className="text-xs text-foreground-500 mt-0.5">{currentCategory.description}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                {currentCategory.matches.map((match) => {
                  const status = statusLabels[match.status] || statusLabels.upcoming;
                  return (
                    <Link
                      key={match.id}
                      to={`/category/${match.category}`}
                      className="block bg-background-50 border border-background-200/80 rounded-lg p-4 md:p-5 hover:border-primary-300 hover:bg-background-100/50 transition-all duration-200 cursor-pointer"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-0.5 text-xs font-medium rounded border ${status.className}`}>
                              {status.text}
                            </span>
                            <span className="text-xs text-foreground-500">{match.league}</span>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="flex-1 text-center">
                              <p className="text-sm font-semibold text-foreground-800">{match.homeTeam}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {match.status === 'live' || match.status === 'finished' ? (
                                <>
                                  <span className="text-lg md:text-xl font-heading font-bold text-primary-500">
                                    {match.homeScore}
                                  </span>
                                  <span className="text-xs text-foreground-400 font-medium">:</span>
                                  <span className="text-lg md:text-xl font-heading font-bold text-foreground-700">
                                    {match.awayScore}
                                  </span>
                                </>
                              ) : (
                                <span className="text-sm font-heading font-bold text-foreground-400">VS</span>
                              )}
                            </div>
                            <div className="flex-1 text-center">
                              <p className="text-sm font-semibold text-foreground-800">{match.awayTeam}</p>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 sm:flex-col sm:items-end">
                          <span className="text-xs text-foreground-500 whitespace-nowrap">
                            {match.status === 'live' ? match.time : match.status === 'upcoming' ? match.time : '全场'}
                          </span>
                          {match.viewers && (
                            <span className="flex items-center gap-1 text-xs text-foreground-400">
                              <i className="ri-eye-line"></i>
                              {(match.viewers / 10000).toFixed(1)}万
                            </span>
                          )}
                          {match.status !== 'finished' && (
                            <span className="px-3 py-1 bg-primary-500 text-white text-xs font-medium rounded hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                              竞猜
                            </span>
                          )}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}